import { IS_MOBILE, NAVBAR_WIDTH, WINDOW_WIDTH } from '@constants/global.js';
import { CollapsePreviewIcon, ExpandPreviewIcon } from '@icons/index.js';
import { viewerResize } from '@signals/viewer';
import { UIDiv } from '@ui';
import { animateValue } from '@utils/AnimationUtil.js';

const defaultOptions = {
  position: '',
  minWidth: 400,
  maxWidth: WINDOW_WIDTH() * 0.8,
  dragThreshold: 30,
  useIndicators: true,
  target: null,
  onCollapse: null,
  onExpand: null,
  onResize: null,
  getCurrentWidth: null,
  styleCursor: true,
  appendTo: null,
  customStyles: '',
};

class Resizer extends UIDiv {
  constructor(options = defaultOptions) {
    super();
    this.options = options;
    this.currentWidth = 730;
    this.originalWidth = this.currentWidth;
    this.isExpanded = false;

    this.setId(`resizer-${this.options.position}`);

    // Dynamic positioning based on options
    const positionClass =
      this.options.position === 'left' ? 'left-0' : 'right-0';
    this.setClass(
      `absolute top-0 ${positionClass} w-2 bottom-0 cursor-ew-resize bg-gray-100 hover:bg-gray-300 transition-colors duration-200 flex items-center z-[10]`,
    );
    if (this.options.customStyles) {
      this.addClass(this.options.customStyles);
    }

    // const afterElement = new UIDiv(); //widget disabled for now
    // afterElement.setClass(
    //   'after:content-[""] after:absolute after:w-1/2 after:h-[20%] after:max-h-[110px] after:bg-[var(--o-hive-primary-60)] after:rounded after:opacity-75 after:translate-x-1/2 after:translate-y-[-50%]',
    // );
    // this.add(afterElement);

    if (this.options.useIndicators) {
      this.createIndicators();
    }

    this.addHoverEffect();
    this.setupDragEvents();
    this.setupWindowResizeHandler();
    return this;
  }
  createIndicators() {
    const createIndicator = IconClass => {
      const indicator = new UIDiv();
      indicator.setClass(
        'text-white p-4 rounded-xl shadow-2xl flex items-center justify-center z-20',
      );
      indicator.setStyle('display', ['none']);
      const icon = new IconClass({
        size: 30,
        color: '#ffffff',
      });
      indicator.add(icon);

      return indicator;
    };

    this.collapseIndicator = createIndicator(CollapsePreviewIcon);
    this.expandIndicator = createIndicator(ExpandPreviewIcon);
  }

  addHoverEffect() {
    this.onMouseOver = () => {
      this.setStyle('backgroundColor', ['rgba(0, 0, 0, 0.1)']);
    };

    this.onMouseLeave = () => {
      this.setStyle('backgroundColor', ['rgba(0, 0, 0, 0)']);
    };
  }

  setupDragEvents() {
    let isDragging = false;
    let startX;
    let startWidth;
    let indicatorOverlay = null;

    const toggleIndicator = (show, isCollapse) => {
      if (!indicatorOverlay || !this.options.useIndicators) return;

      indicatorOverlay.setStyle('opacity', [show ? '1' : '0']);
      this.collapseIndicator.setStyle('display', [
        isCollapse && show ? 'flex' : 'none',
      ]);
      this.expandIndicator.setStyle('display', [
        !isCollapse && show ? 'flex' : 'none',
      ]);
    };

    this.dom.onpointerdown = e => {
      if (e.isPrimary === false) return;

      e.preventDefault();
      e.stopPropagation();

      isDragging = true;
      startX = e.clientX;
      startWidth = this.options.getCurrentWidth();

      if (this.options.styleCursor) {
        document.body.style.cursor = 'ew-resize';
        document.body.style.userSelect = 'none';
      }

      const targetElement = document.getElementById(
        this.options.target || 'sidebar',
      );
      if (targetElement) {
        targetElement.classList.add('no-select');
      }

      if (this.options.useIndicators) {
        indicatorOverlay = new UIDiv();
        indicatorOverlay.setClass(
          'absolute inset-0 bg-gray-100/80 opacity-0 transition-opacity duration-200 flex items-center justify-center z-30',
        );

        this.collapseIndicator.setClass(
          'absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2',
        );
        this.expandIndicator.setClass(
          'absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2',
        );

        indicatorOverlay.add(this.collapseIndicator);
        indicatorOverlay.add(this.expandIndicator);

        const parent = this.options.appendTo
          ? document.querySelector(this.options.appendTo)
          : targetElement || document.body;
        parent.appendChild(indicatorOverlay.dom);
      }

      const overlayDiv = new UIDiv();
      overlayDiv.setClass(
        'fixed top-0 left-0 right-0 bottom-0 z-[9999] cursor-ew-resize',
      );
      overlayDiv.setStyle('pointerEvents', ['all']);
      document.body.appendChild(overlayDiv.dom);
      const maxWidth = this.options.maxWidth;

      const onPointerMove = e => {
        if (!isDragging || e.isPrimary === false) return;

        e.preventDefault();
        const delta = e.clientX - startX;
        let newWidth =
          this.options.position === 'left'
            ? parseFloat(startWidth) - delta
            : parseFloat(startWidth) + delta;
        const beyondMin = this.options.minWidth - newWidth;
        const beyondMax = newWidth - maxWidth;

        // Indicator logic
        if (this.options.useIndicators) {
          if (beyondMin > this.options.dragThreshold) {
            toggleIndicator(true, true);
          } else if (
            beyondMax > this.options.dragThreshold &&
            !this.isExpanded
          ) {
            toggleIndicator(true, false);
          } else {
            toggleIndicator(false);
          }
        }
        newWidth = Math.max(
          this.options.minWidth,
          Math.min(newWidth, maxWidth),
        );
        requestAnimationFrame(() => {
          this.options.onResize?.(newWidth);
        });
      };

      const onPointerUp = e => {
        if (e.isPrimary === false) return;

        isDragging = false;

        if (this.options.styleCursor) {
          document.body.style.cursor = '';
          document.body.style.userSelect = '';
        }

        const targetElement = document.getElementById(
          this.options.target || 'sidebar',
        );
        if (targetElement) {
          targetElement.classList.remove('no-select');
        }

        if (e && e.clientX) {
          const delta =
            this.options.position === 'left'
              ? startX - e.clientX
              : e.clientX - startX;

          const attemptedWidth = startWidth + delta;

          const beyondMin = this.options.minWidth - attemptedWidth;
          const beyondMax = attemptedWidth - maxWidth;

          if (beyondMin > this.options.dragThreshold) {
            this.options?.onCollapse();
          } else if (
            beyondMax > this.options.dragThreshold &&
            !this.isExpanded
          ) {
            if (this.options.onExpand) {
              this.options.onExpand();
            }
          } else {
            this.originalWidth = this.currentWidth;
          }
          viewerResize.dispatch();
        }

        if (indicatorOverlay) {
          indicatorOverlay.dom.parentNode.removeChild(indicatorOverlay.dom);
          indicatorOverlay = null;
        }

        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerUp);
        document.body.removeChild(overlayDiv.dom);
      };

      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerUp);
    };
  }

  animateResize(startWidth, targetWidth, onComplete) {
    return animateValue(
      startWidth,
      targetWidth,
      300,
      currentWidth => this.options.onResize?.(currentWidth),
      onComplete,
    );
  }

  setupWindowResizeHandler() {
    const handleWindowResize = () => {
      // on mobile we want it to take the full width
      this.options.maxWidth = IS_MOBILE()
        ? WINDOW_WIDTH()
        : WINDOW_WIDTH() * 0.8;

      const navbarElement = document.getElementById('navbar');
      if (navbarElement) {
        const navbarWidth = navbarElement.offsetWidth;
        if (navbarWidth > NAVBAR_WIDTH() + 10) {
          this.options.maxWidth = WINDOW_WIDTH() - navbarWidth;
        }
      }

      if (this.currentWidth > this.options.maxWidth) {
        this.currentWidth = this.options.maxWidth;
        this.options.onResize?.(this.currentWidth);
      }
    };

    window.addEventListener('resize', handleWindowResize);
    this.onRemove = () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }
}

export { Resizer };
